//
// # Schematic Clipboard
//
// App-wide (module-level) in-memory clipboard for schematic copy/paste.
// It lives for the lifetime of the browser page and is shared across every
// `Editor`/`Schematic` instance, so it survives mode transitions and content
// reloads, and supports copy in one schematic + paste into another (including
// across tabs in the same window). Not OS-level; not persisted across reloads.
//
// Previously this state lived on the transient `SchematicIdle` mode handler,
// which `Editor.goUiIdle()` re-creates on every mode transition — so the
// clipboard was wiped between Cmd+C and Cmd+V in almost all real flows.
//

import { schdata } from "GenAlphaModel";
import { schema } from "GenAlphaSchema";

// In-memory clipboard payload. Plain data only, no entity references —
// the clipboard outlives the originals if they're deleted before paste.
export interface SchematicClipboard {
  instances: schdata.Instance[];
  wires: schema.schematic.WireSegment[];
  pins: schdata.SchPinSpec[];
}

// A clipboard payload plus the PDK id of the schematic it was copied from.
// `sourcePdkId` is used to gate cross-PDK paste (see `Schematic.paste`).
export interface SchematicClipboardEntry {
  payload: SchematicClipboard;
  sourcePdkId?: string;
}

// The single shared entry. `null` until the first copy.
let current: SchematicClipboardEntry | null = null;

// Shared accessor for the schematic clipboard. A tiny object rather than
// loose functions so call-sites read as `schematicClipboard.get()` etc.
export const schematicClipboard = {
  get(): SchematicClipboardEntry | null {
    return current;
  },
  set(entry: SchematicClipboardEntry): void {
    current = entry;
  },
  clear(): void {
    current = null;
  },
};
